import { useState } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import { useProject } from '@/contexts/project-context'
import { VestingCalculator, VestingStatus } from '@/lib/vesting-calculator'
import VestingTimeline from '@/components/vesting-timeline'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Progress } from '@/components/ui/progress'
import {
  Users,
  Calendar,
  TrendingUp,
  Download,
  Clock,
  Award,
  AlertCircle,
  ChevronRight,
  Zap,
  BarChart3
} from 'lucide-react'
import { format, parseISO } from 'date-fns'
import { toast } from 'sonner'

export default function VestingDashboard() {
  const { currentProject } = useProject()
  const [selectedUser, setSelectedUser] = useState<string | null>(null)
  const [showAccelerationDialog, setShowAccelerationDialog] = useState(false)
  const [accelerationScenario, setAccelerationScenario] = useState({
    trigger: 'acquisition' as 'acquisition' | 'ipo' | 'termination',
    percentage: 100
  })

  // Fetch vesting data
  const { data: vestingStatuses, isLoading } = useQuery({
    queryKey: ['vesting-status', currentProject?.id],
    queryFn: async () => {
      if (!currentProject) return []
      const calculator = new VestingCalculator(currentProject.id)
      return calculator.calculateVestingStatus()
    },
    enabled: !!currentProject
  })

  // Fetch vesting statistics
  const { data: statistics } = useQuery({
    queryKey: ['vesting-statistics', currentProject?.id],
    queryFn: async () => {
      if (!currentProject) return null
      const calculator = new VestingCalculator(currentProject.id)
      return calculator.getVestingStatistics()
    },
    enabled: !!currentProject
  })

  // Fetch milestones for selected user
  const { data: selectedUserMilestones } = useQuery({
    queryKey: ['vesting-milestones', currentProject?.id, selectedUser],
    queryFn: async () => {
      if (!currentProject || !selectedUser) return []
      const calculator = new VestingCalculator(currentProject.id)
      return calculator.getVestingMilestones(selectedUser)
    },
    enabled: !!currentProject && !!selectedUser
  })

  // Simulate acceleration
  const { mutate: simulateAcceleration, data: acceleratedResults } = useMutation({
    mutationFn: async () => {
      if (!currentProject || !selectedUser) throw new Error('No user selected')
      const calculator = new VestingCalculator(currentProject.id)
      return calculator.simulateAcceleratedVesting(
        selectedUser,
        accelerationScenario.trigger,
        accelerationScenario.percentage
      )
    },
    onSuccess: () => {
      toast.success('Acceleration simulation completed')
    },
    onError: () => {
      toast.error('Failed to simulate acceleration')
    }
  })

  const handleExport = async () => {
    if (!currentProject) return
    
    try {
      const calculator = new VestingCalculator(currentProject.id)
      const csv = await calculator.exportVestingSchedule('csv')
      
      const blob = new Blob([csv as string], { type: 'text/csv' })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `vesting-schedule-${format(new Date(), 'yyyy-MM-dd')}.csv`
      a.click()
      window.URL.revokeObjectURL(url)
      
      toast.success('Vesting schedule exported')
    } catch (error) {
      toast.error('Failed to export vesting schedule')
    }
  }

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('en-US').format(Math.round(num))
  }

  const selectedUserStatus = vestingStatuses?.find(s => s.user_id === selectedUser)

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Vesting Dashboard</h1>
          <p className="mt-1 text-sm text-gray-600">
            Track and manage equity vesting schedules
          </p>
        </div>
        <Button onClick={handleExport}>
          <Download className="h-4 w-4 mr-2" />
          Export CSV
        </Button>
      </div>

      {/* Statistics Overview */}
      {statistics && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-medium text-gray-600">
                  Total Participants
                </CardTitle>
                <Users className="h-4 w-4 text-gray-400" />
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">{statistics.total_participants}</p>
              <p className="text-xs text-gray-500 mt-1">
                {statistics.active_vesting_count} actively vesting
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-medium text-gray-600">
                  Total Shares
                </CardTitle>
                <BarChart3 className="h-4 w-4 text-gray-400" />
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">{formatNumber(statistics.total_shares)}</p>
              <Progress 
                value={statistics.overall_vesting_percentage} 
                className="h-2 mt-2"
              />
              <p className="text-xs text-gray-500 mt-1">
                {statistics.overall_vesting_percentage.toFixed(1)}% vested
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-medium text-gray-600">
                  Vested Shares
                </CardTitle>
                <Award className="h-4 w-4 text-green-500" />
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-green-600">
                {formatNumber(statistics.vested_shares)}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                {statistics.fully_vested_count} fully vested
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-medium text-gray-600">
                  Pre-Cliff
                </CardTitle>
                <Clock className="h-4 w-4 text-yellow-500" />
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-yellow-600">
                {statistics.pre_cliff_count}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                {formatNumber(statistics.unvested_shares)} unvested shares
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Vesting Table */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle>Vesting Schedules</CardTitle>
              <CardDescription>
                Click on a participant to view detailed timeline
              </CardDescription>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="space-y-4">
                  {[...Array(3)].map((_, i) => (
                    <div key={i} className="h-16 bg-gray-100 rounded animate-pulse" />
                  ))}
                </div>
              ) : vestingStatuses && vestingStatuses.length > 0 ? (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Participant</TableHead>
                        <TableHead>Progress</TableHead>
                        <TableHead>Vested</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {vestingStatuses.map((status) => (
                        <TableRow
                          key={status.user_id}
                          className="cursor-pointer hover:bg-gray-50"
                          onClick={() => setSelectedUser(status.user_id)}
                        >
                          <TableCell>
                            <div>
                              <p className="font-medium">{status.user_name}</p>
                              <p className="text-sm text-gray-500">{status.email}</p>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="w-32">
                              <Progress 
                                value={status.vesting_percentage} 
                                className="h-2"
                              />
                              <p className="text-xs text-gray-500 mt-1">
                                {status.vesting_percentage.toFixed(1)}%
                              </p>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div>
                              <p className="font-medium">
                                {formatNumber(status.vested_shares)}
                              </p>
                              <p className="text-xs text-gray-500">
                                of {formatNumber(status.total_shares)}
                              </p>
                            </div>
                          </TableCell>
                          <TableCell>
                            {status.is_fully_vested ? (
                              <Badge className="bg-purple-100 text-purple-800">
                                <Award className="h-3 w-3 mr-1" />
                                Fully Vested
                              </Badge>
                            ) : status.cliff_reached ? (
                              <Badge className="bg-green-100 text-green-800">
                                <TrendingUp className="h-3 w-3 mr-1" />
                                Active
                              </Badge>
                            ) : (
                              <Badge className="bg-yellow-100 text-yellow-800">
                                <Clock className="h-3 w-3 mr-1" />
                                Pre-Cliff
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell>
                            <ChevronRight className="h-4 w-4 text-gray-400" />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <div className="text-center py-8">
                  <Users className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-500">No vesting schedules found</p>
                  <p className="text-sm text-gray-400 mt-1">
                    Create equity offers with vesting to see them here
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Selected User Timeline */}
        <div className="lg:col-span-1">
          {selectedUser && selectedUserStatus && selectedUserMilestones ? (
            <VestingTimeline
              status={selectedUserStatus}
              milestones={selectedUserMilestones}
              showAcceleration={!selectedUserStatus.is_fully_vested}
              onSimulateAcceleration={() => setShowAccelerationDialog(true)}
            />
          ) : (
            <Card>
              <CardContent className="text-center py-12">
                <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-500">Select a participant</p>
                <p className="text-sm text-gray-400 mt-1">
                  Click on a row to view vesting timeline
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Acceleration Simulation Dialog */}
      <Dialog open={showAccelerationDialog} onOpenChange={setShowAccelerationDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Simulate Accelerated Vesting</DialogTitle>
            <DialogDescription>
              Model the impact of acceleration triggers on vesting
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Trigger Event</Label>
              <Select
                value={accelerationScenario.trigger}
                onValueChange={(value: any) => setAccelerationScenario({
                  ...accelerationScenario,
                  trigger: value
                })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="acquisition">Acquisition</SelectItem>
                  <SelectItem value="ipo">IPO</SelectItem>
                  <SelectItem value="termination">Termination without Cause</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Acceleration Percentage</Label>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  min="0"
                  max="100"
                  value={accelerationScenario.percentage}
                  onChange={(e) => setAccelerationScenario({
                    ...accelerationScenario,
                    percentage: parseInt(e.target.value) || 0
                  })}
                />
                <span className="text-sm text-gray-500">%</span>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Percentage of unvested shares to accelerate
              </p>
            </div>

            {acceleratedResults && acceleratedResults[0] && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start">
                  <Zap className="h-5 w-5 text-blue-600 mt-0.5 mr-2" />
                  <div>
                    <h4 className="font-medium text-blue-900">Acceleration Impact</h4>
                    <p className="text-sm text-blue-700 mt-1">
                      Additional shares vesting: {formatNumber(
                        acceleratedResults[0].accelerated_vesting?.additional_vested_shares || 0
                      )}
                    </p>
                    <p className="text-sm text-blue-700">
                      New vested total: {formatNumber(acceleratedResults[0].vested_shares)}
                    </p>
                    <p className="text-sm text-blue-700">
                      New vesting percentage: {acceleratedResults[0].vesting_percentage.toFixed(1)}%
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAccelerationDialog(false)}>
              Close
            </Button>
            <Button onClick={() => simulateAcceleration()}>
              <TrendingUp className="h-4 w-4 mr-2" />
              Run Simulation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}